import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client/react";
import { ASSETS_FIND_AND_REPLACE_MUTATION } from "../../../constants/graphql";
import useNotification from "../../../hooks/notification";
import { useBanner } from "../../../contexts/banner";

export default function ReplaceModal({
  open,
  onClose,
  searchResults,
  field,
  findText,
}) {
  const [replaceText, setReplaceText] = useState("");
  const { notifyError } = useNotification();
  const { displayBanner } = useBanner();

  const [findAndReplace, { loading }] = useMutation(ASSETS_FIND_AND_REPLACE_MUTATION, {
    onCompleted: data => {
      const response = data?.assetsFindAndReplace;
      const mutationErrors = response?.errors || [];
      if (mutationErrors.length > 0) {
        notifyError(mutationErrors[0].message || "Failed to update assets");
        return;
      }

      if (response?.success) {
        onClose();
      }
    },
    onError: error => {
      notifyError(error.message || "Failed to update assets");
    },
  });

  const handleReplace = () => {
    if (!field || !findText || !searchResults || searchResults.length === 0) return;

    const assetIds = searchResults.map(asset => asset.id);

    displayBanner({ message: "Processing...", type: "find-and-replace-assets" });

    findAndReplace({
      variables: {
        assetIds,
        fieldName: field,
        findText,
        replaceText,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Find and Replace
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Found {searchResults ? searchResults.length : 0} instances of {field} = {findText}
          </Typography>
          <TextField
            fullWidth
            label="Replace With"
            value={replaceText}
            onChange={e => setReplaceText(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="flex-end" width="100%" p={2}>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={onClose} color="primary">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReplace}
              disabled={loading || !replaceText}
              startIcon={loading && <CircularProgress size={22} />}
            >
              Replace All
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
